import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  NgbActiveModal,
  NgbDropdownModule,
  NgbModalModule,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { ApiService } from 'src/app/core/providers/api/api.service';
import { AppContextService } from 'src/app/core/providers/context/AppContextService ';
import {
  Country,
  CountryListResponse,
} from 'src/app/core/providers/country/country.models';
import { ToastService } from 'src/app/core/providers/toast/toast.service';
import { CardFormComponent } from 'src/app/shared/card-form/card-form.component';
import { getTranslation } from 'src/app/core/helpers/translation.helpers';
import { MessageBusService } from 'src/app/core/providers/busMessage/messageBusService';
import { SharedModule } from 'src/app/shared/shared.module';

@Component({
  selector: 'app-add-firm-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModalModule,
    NgbDropdownModule,
    SharedModule,
    TranslateModule,
    CardFormComponent,
  ],
  templateUrl: './add-firm-modal.component.html',
  styleUrl: './add-firm-modal.component.scss',
})
export class AddFirmModalComponent implements OnInit, OnDestroy {
  subscription: Subscription = new Subscription();
  signupForm: FormGroup;
  countries: Country[] = [];
  registerSuccessful: boolean = false;
  isEditMode = true;
  isSaving = false;
  submitted = false;
  context: any;
  getTranslation = getTranslation;
  mode: 'show' | 'edit' | 'create' = 'create';

  constructor(
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private apiService: ApiService,
    public activeModal: NgbActiveModal,
    private messageBus: MessageBusService,
    public translate: TranslateService,
    private appContextService: AppContextService
  ) {
    this.signupForm = this.formBuilder.group({
      country: ['', Validators.required],
      regNo: [''],
      name: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadCountries();
    this.setupSirenValidation();

    this.appContextService.context$.subscribe((context) => {
      this.context = context;
    });
  }

  private setupSirenValidation(): void {
    this.signupForm
      .get('regNo')
      ?.valueChanges.pipe(
        debounceTime(300),
        filter((value: string | null) => !!value && value.length === 9)
      )
      .subscribe(() => {
        const country = this.signupForm.get('country')?.value;
        const france: any = this.countries.find(
          (country) => country.code === 'FR'
        );

        if (country == france['@id']) {
          this.validateSiren();
        }
      });
  }

  private validateSiren(): void {
    const siren = this.signupForm.get('regNo')?.value;
    this.subscription.add(
      this.apiService.getById('inpi', siren).subscribe({
        next: (response: any) => {
          this.signupForm.patchValue({
            name: response.name,
          });

          this.toastService.show({
            message: this.translate.instant('addFirmModal.papperSuccess'),
            classname: 'bg-success text-light',
            delay: 2500,
          });
        },
      })
    );
  }

  loadCountries(): void {
    this.subscription.add(
      this.apiService
        .getAll('countries', 1, 300, { 'order[name]': 'asc' }, [])
        .subscribe({
          next: (response: CountryListResponse) => {
            this.countries = response.member;
            const france = this.countries.find(
              (country) => country.code === 'FR'
            );
            if (france) {
              this.signupForm.patchValue({ country: france['@id'] });
            }
          },
        })
    );
  }

  onSubmit() {
    this.submitted = true;
    // Vérification si le formulaire est valide
    if (this.signupForm.valid) {
      this.isSaving = true;
      // Récupération des valeurs du formulaire
      let formData = this.signupForm.value;
      formData.country = Array.isArray(formData.country)
        ? formData.country[0]
        : formData.country;

      // Appel à l'API pour ajouter une société
      this.subscription.add(
        this.apiService.create('firms', formData).subscribe({
          next: (response) => {
            this.toastService.show({
              message: this.translate.instant('addFirmModal.saveSuccess'),
              classname: 'bg-success text-light',
              delay: 2500,
            });
            this.messageBus.setMessage({ type: 'CONTEXT_CHANGED' });
            this.activeModal.close(response);
          },
          error: (error) => {
            this.isSaving = false;
            this.toastService.show({
              message: "Une erreur est survenue lors de l'ajout de la société.",
              classname: 'bg-danger text-light',
              delay: 2500,
            });
          },
        })
      );
    }
  }

  closeModal() {
    this.activeModal.close();
  }

  onCancelEdit() {
    this.signupForm.reset();
  }

  get formControls() {
    return this.signupForm.controls;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
